import React from 'react';
import { Helmet } from 'react-helmet-async';
import { 
  Box, 
  Typography, 
  Container,
  Card,
  CardContent,
  alpha,
  useTheme,
} from '@mui/material';

const TermsOfServicePage = () => {
  const theme = useTheme();

  return (
    <>
      <Helmet>
        <title>Terms of Service - RatePly</title>
        <meta name="description" content="RatePly Terms of Service - Rules and conditions for using our currency conversion service." />
      </Helmet>
      
      <Container maxWidth="md" sx={{ py: 4 }}>
        {/* Header */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography
            variant="h3"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 700,
              color: 'white',
              textShadow: '0 2px 4px rgba(0,0,0,0.3)',
              mb: 2,
            }}
          >
            Terms of Service
          </Typography>
          <Typography
            variant="h6"
            sx={{
              color: alpha(theme.palette.common.white, 0.9),
              maxWidth: 600,
              mx: 'auto',
              lineHeight: 1.6,
            }}
          >
            Last updated: {new Date().toLocaleDateString()}
          </Typography>
        </Box>

        {/* Content */}
        <Card
          elevation={4}
          sx={{
            borderRadius: 3,
            background: `linear-gradient(135deg, ${alpha(theme.palette.background.paper, 0.9)} 0%, ${alpha(theme.palette.background.default, 0.9)} 100%)`,
            backdropFilter: 'blur(10px)',
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
              Terms of Service for RatePly
            </Typography>

            <Box sx={{ '& > *': { mb: 3 } }}>
              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  1. Acceptance of Terms
                </Typography>
                <Typography variant="body1" paragraph>
                  By accessing and using RatePly, you accept and agree to be bound by the terms and provision of this agreement. 
                  If you do not agree to abide by the above, please do not use this service.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  2. Description of Service
                </Typography>
                <Typography variant="body1" paragraph>
                  RatePly provides:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Real-time currency exchange rate information</li>
                  <li>Currency conversion calculations</li>
                  <li>Historical exchange rate data</li>
                  <li>Educational content about foreign exchange</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  3. Use License
                </Typography>
                <Typography variant="body1" paragraph>
                  Permission is granted to temporarily use RatePly for personal, non-commercial transitory viewing only. This is the grant of a license, not a transfer of title, and under this license you may not:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Modify or copy the materials</li>
                  <li>Use the materials for commercial purposes or for public display</li>
                  <li>Attempt to reverse engineer any software contained on the website</li>
                  <li>Remove any copyright or proprietary notations from the materials</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  4. Disclaimer
                </Typography>
                <Typography variant="body1" paragraph>
                  The information on this website is provided on an "as is" basis. To the fullest extent permitted by law, RatePly:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>Excludes all representations and warranties relating to this website and its contents</li>
                  <li>Does not guarantee the accuracy of exchange rates</li>
                  <li>Is not responsible for financial decisions made based on our data</li>
                  <li>Recommends verifying rates with financial institutions for important transactions</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  5. Limitations
                </Typography>
                <Typography variant="body1" paragraph>
                  In no event shall RatePly or its suppliers be liable for any damages (including, without limitation, 
                  damages for loss of data or profit, or due to business interruption) arising out of the use or inability 
                  to use the materials on RatePly's website, even if RatePly or an authorized representative has been 
                  notified orally or in writing of the possibility of such damage. Because some jurisdictions do not 
                  allow limitations on implied warranties, or limitations of liability for consequential or incidental 
                  damages, these limitations may not apply to you.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  6. Accuracy of Materials
                </Typography>
                <Typography variant="body1" paragraph>
                  The materials appearing on RatePly's website could include technical, typographical, or photographic errors. 
                  RatePly does not warrant that any of the materials on its website are accurate, complete, or current. 
                  RatePly may make changes to the materials contained on its website at any time without notice. 
                  However, RatePly does not make any commitment to update the materials.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  7. Prohibited Uses
                </Typography>
                <Typography variant="body1" paragraph>
                  You may not use our service:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                  <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
                  <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
                  <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
                  <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
                  <li>To submit false or misleading information</li>
                  <li>To upload or transmit viruses or any other type of malicious code</li>
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  8. Contact Information
                </Typography>
                <Typography variant="body1" paragraph>
                  If you have any questions about these Terms of Service, please contact us at:
                </Typography>
                <Typography variant="body2">
                  Email: terms@rateply.com
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  9. Governing Law
                </Typography>
                <Typography variant="body1" paragraph>
                  These terms and conditions are governed by and construed in accordance with the laws and you 
                  irrevocably submit to the exclusive jurisdiction of the courts in that state or location.
                </Typography>
              </Box>

              <Box>
                <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                  10. Changes to Terms
                </Typography>
                <Typography variant="body1">
                  We reserve the right, at our sole discretion, to modify or replace these Terms at any time. 
                  If a revision is material, we will try to provide at least 30 days notice prior to any new terms taking effect.
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>
      </Container>
    </>
  );
};

export default TermsOfServicePage;
